import React from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { useMutation } from '@apollo/client';
import { gql } from '@apollo/client';
import { useSession } from '../contexts/SessionProvider';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { publicClient } from '~/services';

const GOOGLE_SIGN_IN = gql`
  mutation GoogleSignIn($data: GoogleSignInInputType!) {
    google_sign_in(data: $data) {
      data {
        accessToken
        refreshToken
      }
      hasError
      message
    }
  }
`;

interface GoogleAuthButtonProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
  text?: 'signin_with' | 'signup_with' | 'continue_with';
  size?: 'large' | 'medium' | 'small';
  theme?: 'outline' | 'filled_blue' | 'filled_black';
  shape?: 'rectangular' | 'pill' | 'circle' | 'square';
  className?: string;
  referralCode?: string;
}

export const GoogleAuthButton: React.FC<GoogleAuthButtonProps> = ({
  onSuccess,
  onError,
  text = 'continue_with',
  size = 'large',
  theme = 'outline',
  shape = 'rectangular',
  className = '',
  referralCode,
}) => {
  const { createSession } = useSession();
  const navigate = useNavigate();
  const { t } = useTranslation('login');
  const [googleSignIn, { loading }] = useMutation(GOOGLE_SIGN_IN, {
    client: publicClient,
  });

  const handleGoogleSuccess = async (credentialResponse: any) => {
    try {
      if (!credentialResponse.credential) {
        throw new Error('No credential received from Google');
      }

      const response = await googleSignIn({
        variables: {
          data: {
            idToken: credentialResponse.credential,
            ...(referralCode && { referral_code: referralCode }),
          },
        },
      });

      const { data, hasError, message } = response.data.google_sign_in;

      if (hasError) {
        console.error('Google sign-in error:', message);
        onError?.(message);
        return;
      }

      // Create session using existing session provider
      createSession({ response: { data } });

      // Call success callback
      onSuccess?.();

      // Navigate to dashboard
      navigate('/');

      console.log('Google sign-in successful');
    } catch (error: any) {
      console.error('Google sign-in failed:', error);
      const errorMessage = error.message || t('google_signin_failed');
      onError?.(errorMessage);
    }
  };

  const handleGoogleError = () => {
    console.error('Google Login Failed');
    const errorMessage = t('google_signin_failed');
    onError?.(errorMessage);
  };

  return (
    <div className={`google-auth-button ${className}`}>
      <GoogleLogin
        onSuccess={handleGoogleSuccess}
        onError={handleGoogleError}
        text={text}
        size={size}
        theme={theme}
        shape={shape}
        width="100%"
        logo_alignment="left"
        use_fedcm_for_prompt={true}
      />
      {loading && (
        <div className="mt-2 text-center text-sm text-gray-500">
          {t('signing_in')}...
        </div>
      )}
    </div>
  );
};

export default GoogleAuthButton;
